import { FC, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { RenderIf } from 'components/RenderIf'
import { Button } from 'featureSliced/entities/Button'
import TableSelect from 'featureSliced/features/TableSelect'

import {
  ButtonContainer,
  NotificationContainer,
  WaiterCallPrompt,
} from '../NotificationsModal/styles'
import { useTranslations } from 'featureSliced/shared/hooks/useTranslations'
import { useOpenModal } from 'featureSliced/shared/hooks/useOpenModal'
import { qrMenuPOST } from 'featureSliced/shared/api/qrMenu/POST'

type CallWaiterModalProps = {
  onDeclineClose: () => void
  onSuccessClose: () => void
  isTableTaken?: boolean
}

export const CallWaiterModal: FC<CallWaiterModalProps> = ({
  onDeclineClose,
  onSuccessClose,
  isTableTaken,
}) => {
  // Hooks
  const t = useTranslations()
  const openModal = useOpenModal()
  const [searchParams] = useSearchParams()
  const table = searchParams.get('table')

  // State
  const [isSuccess, setIsSuccess] = useState(false)
  const [isError, setIsError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // Functions
  const callOrPrompt = () => {
    if (table) {
      callWaiter(table)
    } else {
      promptTableId()
    }
  }

  const promptTableId = () => {
    openModal({
      id: 'TABLE_SELECTION',
      title: t('canner.enterTableNumber'),
      shouldHideCross: true,
      components: [
        ({ closeThisModal }) => (
          <TableSelect
            onClose={(tableIdProp) => {
              callWaiter(tableIdProp)
              closeThisModal()
            }}
          />
        ),
      ],
    })
  }

  const callWaiter = async (tableIdProp?: string | number) => {
    if (tableIdProp) {
      try {
        setIsLoading(true)
        const success = await qrMenuPOST.callWaiter(+tableIdProp)
        if (success) {
          setIsSuccess(true)
        }
      } catch (e) {
        setIsError(true)
      } finally {
        setIsLoading(false)
      }
    }
  }

  return (
    <NotificationContainer>
      <RenderIf condition={isSuccess && !isError}>
        <WaiterCallPrompt>{t('mainPage.waiterSuccess')}</WaiterCallPrompt>
        <ButtonContainer>
          <Button buttonType="stroke" width="100%" onClick={onSuccessClose}>
            {t('buttons.ok')}
          </Button>
        </ButtonContainer>
      </RenderIf>
      <RenderIf condition={isError && !isSuccess}>
        <WaiterCallPrompt>{t('errors.smthWentWrong')}...</WaiterCallPrompt>
        <ButtonContainer>
          <Button buttonType="stroke" width="100%" onClick={onDeclineClose}>
            {t('buttons.close')}
          </Button>
        </ButtonContainer>
      </RenderIf>
      <RenderIf condition={!isSuccess && !isError}>
        <WaiterCallPrompt>
          {isTableTaken
            ? t('mainPage.tableIsTakenByMobileWaiter')
            : t('mainPage.waiterHelpPrompt')}
        </WaiterCallPrompt>
        <ButtonContainer>
          <Button
            buttonType="stroke"
            onClick={onDeclineClose}
            disabled={isLoading}
          >
            {t('common.no')}
          </Button>
          <Button
            buttonType="common"
            onClick={callOrPrompt}
            isLoading={isLoading}
          >
            {t('common.yes')}
          </Button>
        </ButtonContainer>
      </RenderIf>
      <RenderIf condition={isSuccess && isError}>
        <ButtonContainer>
          <Button buttonType="stroke" width="100%" onClick={onDeclineClose}>
            {t('buttons.close')}
          </Button>
        </ButtonContainer>
      </RenderIf>
    </NotificationContainer>
  )
}
